// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import { shareApi, ShareRequestError } from '../api/auth';
import { rememberShareToken } from '../app/cases';
import type { ShareLinkPublic } from '../api/types';
import { useTheme } from '../app/useTheme';
import { Button } from '../components/Button';
import controls from '../components/controls.module.css';
import { TextInput } from '../components/TextInput';
import { ThemeToggle } from '../components/ThemeToggle';
import {
  landingExpiryText,
  type LandingFailure,
  landingInfoFailure,
  landingOpenErrorText,
  landingTarget,
  linkLabel,
} from '../deling/model';
import styles from './SharePage.module.css';

type Load = { kind: 'loading' } | { kind: 'failed'; failure: LandingFailure } | { kind: 'ready'; info: ShareLinkPublic };

/**
 * `/del/<token>` — a read-only share link's door (ruxd-wmc.4). Outside the
 * login gate: it asks the server what the link is (and whether it needs a
 * password), opens it — which sets the share cookie — and goes on to the
 * case, where the visitor is a viewer.
 */
/** The server's requested wait, from a refused share-link call. */
function waitOf(cause: unknown): number | null {
  return cause instanceof ShareRequestError ? cause.retryAfterSeconds : null;
}

export function SharePage({ token }: { token: string }) {
  const theme = useTheme();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [opening, setOpening] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setLoad({ kind: 'loading' });
    shareApi.info(token).then(
      (info) => {
        if (!live) return;
        setLoad({ kind: 'ready', info });
        if (info.requires_password) requestAnimationFrame(() => passwordRef.current?.focus());
      },
      (cause: unknown) => live && setLoad({ kind: 'failed', failure: landingInfoFailure(cause, waitOf(cause)) }),
    );
    return () => {
      live = false;
    };
  }, [token, attempt]);

  const open = (e: FormEvent) => {
    e.preventDefault();
    if (load.kind !== 'ready' || opening) return;
    const needs = load.info.requires_password;
    if (needs && password === '') return;
    setFailure(null);
    setOpening(true);
    shareApi.open(token, needs ? password : undefined).then(
      (me) => {
        rememberShareToken(token);
        window.location.replace(landingTarget(me));
      },
      (cause: unknown) => {
        setOpening(false);
        setPassword('');
        setFailure(landingOpenErrorText(cause, needs, waitOf(cause)));
        requestAnimationFrame(() => passwordRef.current?.focus());
      },
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.theme}>
        <ThemeToggle preference={theme.preference} setPreference={theme.setPreference} />
      </div>
      <main className={styles.card} aria-busy={load.kind === 'loading' || opening}>
        <div className={styles.brand}>
          <span className={styles.product}>
            ReUse<em className={styles.x}>X</em>
          </span>
          <span className={styles.tagline}>Ressourcekortlægning af bygninger</span>
        </div>

        {load.kind === 'loading' && (
          <>
            <h1 className={styles.title}>Delt sag</h1>
            <p className={styles.notice} role="status">
              Tjekker linket…
            </p>
          </>
        )}

        {load.kind === 'failed' && (
          <>
            <h1 className={styles.title}>{load.failure.title}</h1>
            <p className={styles.help}>{load.failure.text}</p>
            {load.failure.retry && (
              <Button variant="outline" size="lg" block className={styles.secondary} onClick={retry}>
                Prøv igen
              </Button>
            )}
          </>
        )}

        {load.kind === 'ready' && (
          <>
            <div className={styles.head}>
              <span className={styles.eyebrow}>Delt sag</span>
              <h1 className={styles.label}>{linkLabel(load.info.label)}</h1>
              <span className={styles.expiry}>{landingExpiryText(load.info)}</span>
            </div>
            <p className={styles.help}>
              Linket giver læseadgang: du kan se sagens kortlægning, prøver og rapport, men ikke ændre noget.
            </p>
            <form className={styles.form} onSubmit={open} noValidate>
              {load.info.requires_password && (
                <label className={controls.field}>
                  <span className={controls.fieldLabel}>Adgangskode</span>
                  <TextInput
                    ref={passwordRef}
                    variant="outline"
                    size="lg"
                    block
                    className={styles.input}
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={opening}
                    required
                  />
                </label>
              )}
              {failure && (
                <p className={styles.error} role="alert">
                  {failure}
                </p>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                className={styles.submit}
                disabled={opening || (load.info.requires_password && password === '')}
              >
                {opening ? 'Åbner…' : 'Åbn sagen'}
              </Button>
            </form>
            {load.info.requires_password && (
              <p className={styles.help}>Adgangskoden får du af den, der sendte linket.</p>
            )}
          </>
        )}
      </main>
    </div>
  );
}
